import { useCallback, useState } from "react";
import { type FinalizeVaultDocumentMutation } from "#graphql/supergraphTypes";
import { MULTIPART_THRESHOLD_BYTES } from "#helpers/upload-multipart";
import { putBytesToTarget } from "./putBytesToTarget";
import { useFinalizeVaultDocument } from "./useFinalizeVaultDocument";
import { useReplaceVaultDocument } from "./useReplaceVaultDocument";

type FinalizedDocument = FinalizeVaultDocumentMutation["finalizeVaultDocument"];

/**
 * `replaceVaultDocument` has no multipart path (see the module doc below): a
 * file at or above the size that would switch `createVaultDocument` onto
 * multipart instead fails here — either the mutation rejects outright, or it
 * returns no `uploadTarget` and the caller sees the opaque "no upload target"
 * error, which really means "too large for the replace path". Guarded
 * client-side with a clear message instead of letting either happen. Reads the
 * shared threshold so it cannot drift from the create flow's multipart branch,
 * and keeps its own name because "the replace path's ceiling" is the meaning
 * callers here care about.
 */
export const REPLACE_MULTIPART_THRESHOLD_BYTES = MULTIPART_THRESHOLD_BYTES;

export interface UploadVaultDocumentVersionArgs {
  publicRefId: string;
  file: File;
  /**
   * "What changed?" note. Persisted on `finalizeVaultDocument`, per the Mode A
   * contract described on {@link useReplaceVaultDocument} — never sent to
   * `replaceVaultDocument` itself, which ignores it whenever new bytes are
   * involved.
   */
  changeNote?: string;
}

export interface UseUploadVaultDocumentVersionReturn {
  /**
   * Full replace flow (Mode A only): `replaceVaultDocument` -> PUT the bytes
   * to the presigned target -> `finalizeVaultDocument` with the change note.
   * Resolves to the finalized document (its `status` reflects the
   * guaranteed-attribute validation: validated or quarantined). Rejects (and
   * sets `error`) if any step fails.
   */
  uploadVersion: (args: UploadVaultDocumentVersionArgs) => Promise<FinalizedDocument | null>;
  uploading: boolean;
  error: Error | null;
}

/**
 * DV-548 — mints a new version of an EXISTING document with new bytes,
 * mirroring {@link useUploadVaultDocument}'s create-flow shape for the replace
 * flow. There is no multipart path here: unlike `createVaultDocument`,
 * `replaceVaultDocument`'s output carries only a single-part `uploadTarget`
 * (no `multipartPlan`), so a missing upload target is always an error rather
 * than a signal to fall back to part-signing.
 *
 * @example
 * const { uploadVersion, uploading } = useUploadVaultDocumentVersion();
 * await uploadVersion({ publicRefId, file, changeNote: note });
 */
export const useUploadVaultDocumentVersion = (): UseUploadVaultDocumentVersionReturn => {
  const { replaceVaultDocument } = useReplaceVaultDocument();
  const { finalizeVaultDocument } = useFinalizeVaultDocument();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadVersion = useCallback(
    async ({ publicRefId, file, changeNote }: UploadVaultDocumentVersionArgs) => {
      setUploading(true);
      setError(null);
      try {
        if (file.size >= REPLACE_MULTIPART_THRESHOLD_BYTES) {
          throw new Error(
            "Files 100 MB or larger can't be uploaded as a new version yet — try a smaller file.",
          );
        }
        const replaced = await replaceVaultDocument({
          publicRefId,
          contentType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          filename: file.name,
        });

        if (!replaced?.uploadTarget) {
          throw new Error("replaceVaultDocument returned no upload target");
        }
        await putBytesToTarget(replaced.uploadTarget, file);

        return await finalizeVaultDocument({
          publicRefId,
          ssbVersionNumber: replaced.ssbVersionNumber,
          changeNote,
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [replaceVaultDocument, finalizeVaultDocument],
  );

  return { uploadVersion, uploading, error };
};
